import React, { useEffect, useRef } from 'react';
import {
  HeaderGroup,
  Row,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import classNames from 'classnames';

import { useComponentTheme } from '../../context';
import { Pagination } from './pagination';
import { HeaderSelectCheckbox, RowSelectCheckbox } from './selection';
import {
  defaultIsRowSelectable,
  defaultPropGetter,
  getSortByRule,
  getSortByString,
} from './table.util';
import { TableHeader } from './table-header.component';
import { TableRows } from './table-rows.component';
import { TableState } from './table-state.component';

export type TableState = {
  pageSize: number;
  pageIndex: number;
  selectedRowIds: Record<string, boolean>;
  sortBy: string;
};

export type TableProps = {
  columns: any[];
  data?: any[];
  isLoading?: boolean;
  isFetching?: boolean;
  isPreviousData?: boolean;
  error?: unknown;
  pageCount?: number;
  totalCount?: number;
  rowsPerPage?: number[];
  pageSize?: number;
  pageIndex?: number;
  enableSelection?: boolean;
  selectedRowIds?: Record<string, boolean>;
  sortBy?: string;
  disableSortRemove?: boolean;
  loadingState?: React.ReactNode;
  emptyState?: React.ReactNode;
  errorState?: React.ReactNode;
  showPagination?: boolean;
  hasRowDividers?: boolean;
  hightlightRowOnHover?: boolean;
  removeMinHeight?: boolean;
  hiddenColumns?: string[];
  showHeading?: boolean;
  className?: string;
  getRowProps?: (row: Row<any>) => void;
  getRowId?: (
    originalRow: any,
    relativeIndex: number,
    parent?: Row<any>
  ) => string;
  onStateChange?: (state: TableState) => void;
  isRowSelectable?: (row: Row<any>) => boolean;
  headerRenderer?: (headerGroups: HeaderGroup<any>[]) => React.ReactElement;
};

export type TableThemeProps = Pick<
  TableProps,
  'hasRowDividers' | 'hightlightRowOnHover'
>;

export const Table = (props: TableProps) => {
  const {
    columns,
    data = [],
    pageCount: controlledPageCount,
    totalCount,
    pageSize: controlledPageSize = 50,
    pageIndex: controlledPageIndex = 0,
    rowsPerPage = [controlledPageSize],
    sortBy: controlledSortBy,
    enableSelection,
    selectedRowIds: controlledSelectedRowIds,
    disableSortRemove,
    isLoading,
    isFetching,
    isPreviousData,
    error,
    loadingState,
    emptyState,
    errorState,
    removeMinHeight,
    hiddenColumns = [],
    showHeading = true,
    showPagination = true,
    className,
    getRowProps = defaultPropGetter,
    getRowId,
    onStateChange,
    isRowSelectable = defaultIsRowSelectable,
    headerRenderer,
  } = props;
  const hasError = Boolean(error);
  const showFetchingOverlay = isFetching && isPreviousData && !isLoading;
  const { hasRowDividers, hightlightRowOnHover } = useComponentTheme(
    'table',
    props
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    canPreviousPage,
    canNextPage,
    dispatch,
    nextPage,
    previousPage,
    setPageSize,
    setSortBy,
    gotoPage,
    state: { pageIndex, pageSize, selectedRowIds, sortBy },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        hiddenColumns,
        pageIndex: controlledPageIndex,
        pageSize: controlledPageSize,
        selectedRowIds: controlledSelectedRowIds || {},
        ...(controlledSortBy && { sortBy: [getSortByRule(controlledSortBy)] }),
      },
      getRowId,
      manualPagination: true,
      manualSortBy: true,
      disableMultiSort: true,
      disableSortRemove,
      pageCount: controlledPageCount,
      autoResetSelectedRows: false,
      stateReducer: (newState, action) => {
        switch (action.type) {
          case 'setControlledSelectedRowIds':
            return action.payload === newState.selectedRowIds || !action.payload
              ? newState
              : { ...newState, selectedRowIds: action.payload };
          default:
            return newState;
        }
      },
    },
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      if (enableSelection) {
        hooks.visibleColumns.push((columns) => [
          {
            id: 'selection',
            Header: ({ toggleRowSelected, rows }) => (
              <HeaderSelectCheckbox
                toggleRowSelected={toggleRowSelected}
                rows={rows}
                isRowSelectable={isRowSelectable}
              />
            ),
            Cell: ({ row }) => (
              <RowSelectCheckbox row={row} isRowSelectable={isRowSelectable} />
            ),
          },
          ...columns,
        ]);
      }
    }
  );
  const isFirstRunRef = useRef(true);

  useEffect(() => {
    // skip first run of fetchData when the initialState is still correct
    if (isFirstRunRef.current) {
      isFirstRunRef.current = false;
    } else {
      onStateChange?.({
        pageSize,
        pageIndex,
        selectedRowIds,
        sortBy: sortBy.length > 0 ? getSortByString(sortBy[0]) : '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize, selectedRowIds, sortBy]);

  useEffect(() => {
    if (
      controlledPageIndex !== undefined &&
      controlledPageIndex !== pageIndex
    ) {
      gotoPage(controlledPageIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledPageIndex]);

  useEffect(() => {
    if (controlledPageSize !== undefined && controlledPageSize !== pageSize) {
      setPageSize(controlledPageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledPageSize]);

  useEffect(() => {
    if (controlledSortBy !== undefined) {
      if (controlledSortBy === '' && sortBy.length > 0) {
        setSortBy([]);
      } else if (
        controlledSortBy !== '' &&
        getSortByString(sortBy[0]) !== controlledSortBy
      ) {
        setSortBy([getSortByRule(controlledSortBy)]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledSortBy]);

  useEffect(() => {
    dispatch({
      type: 'setControlledSelectedRowIds',
      payload: controlledSelectedRowIds,
    });
  }, [controlledSelectedRowIds, dispatch]);

  return (
    <>
      <div
        className="w-full overflow-x-auto relative"
        style={removeMinHeight ? {} : { minHeight: '14rem' }}
      >
        <table {...getTableProps()} className={classNames('w-full', className)}>
          {showHeading && (
            <thead>
              {headerRenderer ? (
                headerRenderer(headerGroups)
              ) : (
                <TableHeader headerGroups={headerGroups} />
              )}
            </thead>
          )}
          {!isLoading && !hasError && (
            <tbody
              {...getTableBodyProps()}
              className={classNames('text-sm', {
                'opacity-0': showFetchingOverlay,
              })}
            >
              <TableRows
                rows={rows}
                hightlightRowOnHover={hightlightRowOnHover}
                hasRowDividers={hasRowDividers}
                prepareRow={prepareRow}
                getRowProps={getRowProps}
              />
            </tbody>
          )}
        </table>

        <TableState
          isLoading={isLoading}
          loadingState={loadingState}
          showFetchingOverlay={showFetchingOverlay}
          hasError={hasError}
          errorState={errorState}
          error={error}
          data={data}
          emptyState={emptyState}
        />
      </div>

      {showPagination && data.length > 0 && !isLoading && !hasError ? (
        <div className="p-6 flex items-center text-sm">
          <Pagination
            pageSize={pageSize}
            pageIndex={pageIndex}
            onNextPage={() => nextPage()}
            onPreviousPage={() => previousPage()}
            onPageChange={(value) => gotoPage(value)}
            canPreviousPage={canPreviousPage}
            canNextPage={canNextPage}
            totalCount={totalCount}
            rowsPerPage={rowsPerPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      ) : null}
    </>
  );
};
